import { useEffect, useMemo, useState } from 'react';
import { fetchCatalogModels, type Job3D } from './threeDApi';

// Which catalog items have a 3D model in the Parts DB — the one fact both the 3D phase
// and its progress bar are about.
//
// It is deliberately NOT the run history. A part with three reconstructions and none of
// them switched on has nothing Phase 6 can assemble from, and a part whose only mesh came
// from the other engine still has one: what counts is the curated subset, which is exactly
// what /api/notice/3d/catalog answers and what the switch under the viewer writes.
//
// Module-scope and shared, like the catalog itself: the phase bar reads it without the
// phase being open, and the phase writes it while the bar is watching.

// THE RUNS THEMSELVES, not just their item ids. The set below is derived from this rather
// than fetched beside it: the assembly phase needs the JOB behind each part — a model has
// to be downloaded to be placed, and its bytes are addressed by job id — while the bar
// beside it only ever asks whether there is one. Two reads of the same endpoint would be a
// second request and, worse, two answers that can disagree for as long as one is stale.
let cache: Job3D[] | null = null;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

function load(): Promise<void> {
  if (cache) return Promise.resolve();
  if (!inflight) {
    inflight = fetchCatalogModels()
      .then((models) => {
        cache = models.filter((m) => m.ready && m.itemId != null);
      })
      // A failed read leaves the cache NULL rather than empty, for the same reason the
      // catalog's does: "nothing is in the database" is an answer, and a read that never
      // landed is not one. `loaded` below is what tells the two apart.
      .catch(() => {})
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
}

// The Parts DB's meshes changed: forget them, and tell whoever is looking. Called after
// the switch under the viewer, so the part selector's dots and the phase bar move with it
// instead of waiting for a reload.
export function invalidatePartModels() {
  cache = null;
  inflight = null;
  listeners.forEach((notify) => notify());
}

// The curated runs, fetched once and shared. `loaded` is whether this is evidence — before
// the first read lands there is no answer here at all, and a caller asking "does this part
// still need one?" must not be told yes by an empty list.
export function useCatalogModels(): { models: Job3D[]; loaded: boolean } {
  const [runs, setRuns] = useState<Job3D[] | null>(cache);
  useEffect(() => {
    let alive = true;
    const read = () => {
      void load().then(() => {
        if (alive) setRuns(cache);
      });
    };
    const onChange = () => {
      if (alive) setRuns(null);
      read();
    };
    listeners.add(onChange);
    read();
    return () => {
      alive = false;
      listeners.delete(onChange);
    };
  }, []);
  return { models: runs ?? EMPTY_RUNS, loaded: runs !== null };
}

// The item ids with a model — the phase bar's question, and the part selector's dots.
export function usePartModels(): { itemIds: Set<number>; loaded: boolean } {
  const { models, loaded } = useCatalogModels();
  const itemIds = useMemo(
    () => new Set(models.map((m) => m.itemId as number)),
    [models],
  );
  return { itemIds, loaded };
}

// WHICH RUN IS EACH PART'S MODEL: item id -> the run itself. The assembly phase's question,
// because a part cannot be placed in a scene without being downloaded first.
//
// THE WHOLE RUN AND NOT ITS ID, which is not generality for its own sake. Two things about
// a mesh are needed to draw it beside another one: where its bytes are (`id`, see meshSrc)
// and whether it is already at true scale (`model`, see isTrueScale) — a CAD or lathe mesh
// carries its real size in the file, and handing on the id alone loses that. It costs
// nothing to keep, and having lost it once is the reason it is said here.
//
// ONE PER PART, THE NEWEST. The server keeps one model per part (per colourway, for a piece
// of a product — see set_in_catalog), and the list comes newest first. Where it still holds
// two, the first one read wins: the newest, which is the one the add-on takes too. It was the
// last one read, which was the OLDEST — the page and the add-on picking different models of
// one part without either saying so.
export function usePartModelJobs(): { modelOf: Map<number, Job3D>; loaded: boolean } {
  const { models, loaded } = useCatalogModels();
  const modelOf = useMemo(() => {
    const out = new Map<number, Job3D>();
    for (const m of models) if (!out.has(m.itemId as number)) out.set(m.itemId as number, m);
    return out;
  }, [models]);
  return { modelOf, loaded };
}

const EMPTY_RUNS: Job3D[] = [];
